import { useRef } from 'react';
import { IconCheck, IconPalette } from '@tabler/icons-react';

// Fallback palette used when a colorpicker field carries no preset swatches.
const DEFAULT_SWATCHES = [
    '#FFFFFF', '#000000', '#C0392B', '#E84393', '#F1C40F',
    '#27AE60', '#16A085', '#2E5AAC', '#4AA3DF', '#8E44AD',
    '#F5F0E6', '#95A5A6',
];

/**
 * A row of clickable color swatches + a "custom color" button.
 * - `value`         current hex ('' when the user hasn't picked yet)
 * - `defaultColor`  hex shown as pre-selected when `value` is empty
 * - `swatches`      preset hex list (falls back to a general palette when empty)
 * - `onChange(hex)` called with the chosen hex
 * - `size`          'sm' (admin test panel) | 'md' (user panel)
 */
export default function ColorSwatchPicker({ value, defaultColor, swatches = [], onChange, size = 'md' }) {
    const inputRef = useRef(null);
    const current = (value || defaultColor || '').toString();
    const list = (swatches && swatches.length ? swatches : DEFAULT_SWATCHES).filter(Boolean);
    // Keep the default color available even if it isn't in the preset list.
    if (defaultColor && !list.some(c => c.toLowerCase() === defaultColor.toLowerCase())) {
        list.unshift(defaultColor);
    }
    const box = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
    const isCustom = current && !list.some(c => c.toLowerCase() === current.toLowerCase());

    return (
        <div className="flex flex-wrap items-center gap-2">
            {list.map((hex) => {
                const selected = current.toLowerCase() === hex.toLowerCase();
                return (
                    <button
                        key={hex}
                        type="button"
                        onClick={() => onChange(hex)}
                        title={hex}
                        style={{ backgroundColor: hex }}
                        className={`relative ${box} rounded-lg border transition-shadow ${selected
                            ? 'border-primary ring-2 ring-primary/40'
                            : 'border-zinc-200 dark:border-zinc-700 hover:border-primary/50'}`}
                    >
                        {selected && (
                            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-2 border-white dark:border-zinc-900">
                                <IconCheck size={9} className="text-white" />
                            </span>
                        )}
                    </button>
                );
            })}

            {/* Custom color */}
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                title="Custom color"
                className={`relative ${box} rounded-lg border flex items-center justify-center text-white bg-gradient-to-br from-indigo-500 to-fuchsia-500 ${isCustom
                    ? 'border-primary ring-2 ring-primary/40'
                    : 'border-zinc-200 dark:border-zinc-700'}`}
            >
                <IconPalette size={size === 'sm' ? 13 : 16} />
                {isCustom && (
                    <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-2 border-white dark:border-zinc-900">
                        <IconCheck size={9} className="text-white" />
                    </span>
                )}
            </button>
            <input
                ref={inputRef}
                type="color"
                className="sr-only"
                value={current || '#000000'}
                onChange={e => onChange(e.target.value)}
            />
        </div>
    );
}
